/* ============================================
   Centrales de Costo — Full CRUD Page
   Manages cost centers with percentage allocation,
   colored progress bars, and inline editing
   ============================================ */
import { useState, useMemo } from 'react'
import { useSupabase } from '@/hooks/useSupabase'
import { useCachedData } from '@/hooks/useCachedData'
import { showToast } from '@/components/layout/Toast'
import EmptyState from '@/components/shared/EmptyState'
import { KpiRow, KpiStrip, PageHeader, StatCard } from '@/components/ui'
import { IconBarChart, IconPlus, IconEdit, IconTrash, IconCheck, IconAlertTriangle, IconSearch } from '@/components/icons/Icons'
import type { CentralCosto } from '@/lib/types'
import { r2 } from '@/lib/utils'

const CRUMBS = [{ label: 'Contabilidad', to: '/contabilidad' }, { label: 'Centrales de Costo' }]

const emptyCentral = (): Omit<CentralCosto, 'id' | 'created_at'> => ({
  nombre: '',
  codigo: '',
  porcentaje: 0,
  descripcion: null,
  activa: true,
  orden: 0,
})

export default function CentralesCosto() {
  const db = useSupabase()

  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<CentralCosto | null>(null)
  const [form, setForm] = useState(emptyCentral())
  const [saving, setSaving] = useState(false)

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<CentralCosto | null>(null)

  // Filtros
  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('todos')

  // En cache: las altas y bajas siguen llamando a `load()`, que ahora revalida.
  const { data: centrales = [], isLoading: loading, refrescar: load } = useCachedData(
    ['centrales_costo'],
    () => db.getAll<CentralCosto>('centrales_costo')
      .then(d => d.sort((a, b) => a.orden - b.orden))
      .catch(() => {
        showToast('Error al cargar centrales', 'error')
        return [] as CentralCosto[]
      }),
  )

  const totalPct = r2(centrales.filter(c => c.activa).reduce((s, c) => s + c.porcentaje, 0))
  const remainingPct = r2(100 - totalPct)
  const activasCount = centrales.filter(c => c.activa).length

  const filtered = useMemo(() => {
    let result = centrales
    if (filtroEstado !== 'todos') result = result.filter(c => (filtroEstado === 'activa' ? c.activa : !c.activa))
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(c =>
        c.nombre?.toLowerCase().includes(q) ||
        c.codigo?.toLowerCase().includes(q) ||
        c.descripcion?.toLowerCase().includes(q)
      )
    }
    return result
  }, [centrales, search, filtroEstado])

  const hayFiltros = !!search || filtroEstado !== 'todos'
  const limpiarFiltros = () => { setSearch(''); setFiltroEstado('todos') }

  // Open modal for create/edit
  const openCreate = () => {
    setEditing(null)
    setForm({ ...emptyCentral(), orden: centrales.length + 1 })
    setShowModal(true)
  }

  const openEdit = (c: CentralCosto) => {
    setEditing(c)
    setForm({
      nombre: c.nombre,
      codigo: c.codigo,
      porcentaje: c.porcentaje,
      descripcion: c.descripcion,
      activa: c.activa,
      orden: c.orden,
    })
    setShowModal(true)
  }

  const closeModal = () => { setShowModal(false); setEditing(null) }

  const saveForm = async () => {
    if (!form.nombre.trim()) { showToast('El nombre es obligatorio', 'error'); return }
    if (!form.codigo.trim()) { showToast('El codigo es obligatorio', 'error'); return }
    setSaving(true)
    try {
      if (editing) {
        await db.update('centrales_costo', { id: editing.id, ...form })
        showToast('Central actualizada', 'success')
      } else {
        await db.add('centrales_costo', form as Record<string, unknown>)
        showToast('Central creada', 'success')
      }
      closeModal()
      await load()
    } catch (err) { showToast(err instanceof Error ? err.message : 'Error al guardar', 'error') }
    finally { setSaving(false) }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await db.remove('centrales_costo', deleteTarget.id)
      showToast('Central eliminada', 'success')
      setDeleteTarget(null)
      await load()
    } catch (err) { showToast(err instanceof Error ? err.message : 'Error al eliminar', 'error') }
  }

  const toggleActiva = async (c: CentralCosto) => {
    try {
      await db.update('centrales_costo', { id: c.id, activa: !c.activa })
      await load()
      showToast(`Central ${!c.activa ? 'activada' : 'desactivada'}`, 'info')
    } catch { showToast('Error al actualizar', 'error') }
  }

  /** Bar fill color based on percentage bracket */
  const barColor = (pct: number) => {
    if (pct <= 15) return ''
    if (pct <= 30) return 'cost-center-bar__fill--success'
    if (pct <= 50) return 'cost-center-bar__fill--warning'
    return 'cost-center-bar__fill--danger'
  }

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter page-shell centrales-costo-page">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={filtered.length === centrales.length
          ? `${centrales.length} central${centrales.length !== 1 ? 'es' : ''}`
          : `${filtered.length} de ${centrales.length}`}
        actions={
          <button className="btn btn-primary" onClick={openCreate}>
            <IconPlus size={16} /> Nueva Central
          </button>
        }
      />

      <KpiStrip>
        <KpiRow>
          <StatCard label="Centrales activas" value={activasCount} />
          <StatCard label="% asignado" value={`${totalPct}%`}
            tone={Math.abs(totalPct - 100) < 0.01 ? 'success' : 'warning'} />
          <StatCard label="% restante" value={`${remainingPct}%`}
            tone={remainingPct === 0 ? 'success' : remainingPct > 0 ? 'warning' : 'danger'} />
        </KpiRow>
      </KpiStrip>

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por nombre, código o descripción..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 165 }}>
          <option value="todos">Activas e inactivas</option>
          <option value="activa">Solo activas</option>
          <option value="inactiva">Solo inactivas</option>
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {centrales.length === 0 ? (
        <EmptyState
          icon={<IconBarChart size={48} />}
          title="Sin centrales de costo"
          description="Crea tu primera central de costo para distribuir los ingresos por área"
          actionLabel="+ Nueva Central"
          onAction={openCreate}
        />
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <h3>Sin resultados</h3>
          <p>Ninguna central coincide con el filtro aplicado.</p>
          <button className="btn btn-secondary" onClick={limpiarFiltros}>Limpiar filtros</button>
        </div>
      ) : (
        <div className="centrales-costo-grid page-shell__table">
          {filtered.map(c => (
            <div className={`centrales-costo-card ${!c.activa ? 'centrales-costo-card--inactive' : ''}`} key={c.id}>
              <div className="centrales-costo-card__header">
                <div className="centrales-costo-card__info">
                  <span className="centrales-costo-card__code">{c.codigo}</span>
                  <h3 className="centrales-costo-card__name">{c.nombre}</h3>
                </div>
                <div className="centrales-costo-card__actions">
                  <button
                    className={`centrales-costo-card__toggle ${c.activa ? 'centrales-costo-card__toggle--on' : ''}`}
                    onClick={() => toggleActiva(c)}
                    title={c.activa ? 'Desactivar' : 'Activar'}
                    type="button"
                  >
                    <span className="centrales-costo-card__toggle-dot" />
                  </button>
                  <button className="btn btn-sm centrales-costo-card__btn" onClick={() => openEdit(c)} type="button">
                    <IconEdit size={14} />
                  </button>
                  <button className="btn btn-sm centrales-costo-card__btn centrales-costo-card__btn--danger" onClick={() => setDeleteTarget(c)} type="button">
                    <IconTrash size={14} />
                  </button>
                </div>
              </div>

              {c.descripcion && (
                <p className="centrales-costo-card__desc">{c.descripcion}</p>
              )}

              <div className="cost-center-bar">
                <div className="cost-center-bar__header">
                  <span className="cost-center-bar__label">Porcentaje asignado</span>
                  <span className="cost-center-bar__value">{c.porcentaje}%</span>
                </div>
                <div className="cost-center-bar__track">
                  <div
                    className={`cost-center-bar__fill ${barColor(c.porcentaje)}`}
                    style={{ width: `${Math.min(c.porcentaje, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer total badge */}
      {centrales.length > 0 && (
        <div className="centrales-costo-footer">
          <div className={`centrales-costo-footer__badge ${Math.abs(totalPct - 100) < 0.01 ? 'centrales-costo-footer__badge--ok' : 'centrales-costo-footer__badge--warn'}`}>
            {Math.abs(totalPct - 100) < 0.01 ? <IconCheck size={16} /> : <IconAlertTriangle size={16} />}
            <span>Total asignado: {totalPct}%</span>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal centrales-costo-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar Central' : 'Nueva Central de Costo'}</h2>
              <button className="modal-close" onClick={closeModal} type="button">x</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Nombre *</label>
                  <input className="form-input" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Operaciones" />
                </div>
                <div className="form-group">
                  <label className="form-label">Codigo *</label>
                  <input className="form-input" value={form.codigo} onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))} placeholder="Ej: OPS" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Porcentaje (%)</label>
                  <input className="form-input" type="number" step="0.01" value={form.porcentaje} onChange={e => setForm(f => ({ ...f, porcentaje: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Orden</label>
                  <input className="form-input" type="number" value={form.orden} onChange={e => setForm(f => ({ ...f, orden: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Descripcion</label>
                <textarea className="form-textarea" value={form.descripcion ?? ''} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value || null }))} placeholder="Descripcion de la central..." />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={form.activa} onChange={e => setForm(f => ({ ...f, activa: e.target.checked }))} />
                  Activa
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal} type="button">Cancelar</button>
              <button className="btn btn-primary" onClick={saveForm} disabled={saving} type="button">{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="modal-backdrop" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h2>Eliminar Central</h2>
              <button className="modal-close" onClick={() => setDeleteTarget(null)} type="button">x</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Estas seguro de que deseas eliminar la central <strong style={{ color: 'var(--text-primary)' }}>{deleteTarget.nombre}</strong> ({deleteTarget.codigo})?
                Esta accion no se puede deshacer.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)} type="button">Cancelar</button>
              <button className="btn btn-danger" onClick={confirmDelete} type="button">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
